import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { writeAdminAuditEvent } from 'lib/adminAudit';
import { getExternalDb } from 'lib/firebase';
import { markStaticSeoRefreshRequired } from 'lib/seoRefreshStatus';
import { requestPublicCourse } from 'lib/siteCoursesApi';

const SITE_CONTENT_COLLECTION = 'site_content';

const DEFAULT_HERO_IMAGE = '/images/home/home-hero-white-truck.png';

export const SITE_CONTENT_KEYS = [
  {
    key: 'homeHero',
    label: 'Homepage hero',
    route: '/',
  },
  {
    key: 'aboutHero',
    label: 'About hero',
    route: '/about',
  },
  {
    key: 'resourcesHero',
    label: 'Resources hero',
    route: '/resources',
  },
  {
    key: 'paperworkHero',
    label: 'Paperwork hero',
    route: '/paperwork',
  },
  {
    key: 'servicesHero',
    label: 'Services hero',
    route: '/services',
  },
  {
    key: 'webinarsHero',
    label: 'Courses hero',
    route: '/webinars',
  },
  {
    key: 'careersHero',
    label: 'Work With Us hero',
    route: '/work-with-us',
  },
  {
    key: 'contactHero',
    label: 'Contact hero',
    route: '/contact',
  },
  {
    key: 'companiesHero',
    label: 'Company review hero',
    route: '/companies/apply',
  },
];

export const DEFAULT_SITE_CONTENT = {
  homeSpeakers: { status: 'published', eyebrow: "Course speakers", title: "Meet the people teaching the work.", body: "Read each speaker's background and teaching topics to see how their experience relates to the responsibilities you want to learn." },
  homeMasterclass: { status: 'published', eyebrow: "Courses", title: "Learn the office work behind every load.", body: "Choose a recorded course on driver records, onboarding, Safety, recruiting, dispatch handoffs or management. Each course costs $299 per person; the current ten-course series costs $1,499 per person. Recorded videos and learning material are included. Access lasts three months from receipt of payment." },
  homeHero: {
    status: 'published',
    public: true,
    eyebrow: 'MAGELLAN INTERNATIONAL',
    title: 'Build a company your team can run.',
    body: [
      'You hired people to help. Too many routine questions still come back to you.',
      'Magellan International helps US trucking companies organize the office behind the fleet through company paperwork, practical courses, staffing and CarrierFlow TMS.',
      'A new hire needs to know what to check, which decisions they can make and when another department needs to take over.',
      'Written procedures give people a reference. Training helps them use it, staffing adds capacity, and software keeps information with the task. Choose the support your company needs.',
    ].join('\n\n'),
    imageUrl: DEFAULT_HERO_IMAGE,
    imageAlt: 'White semi truck travelling on a highway beside green fields under a blue sky',
    primaryCtaLabel: 'View paperwork packages',
    primaryCtaPath: '/paperwork#paperwork-hero',
    secondaryCtaLabel: 'Explore CarrierFlow TMS',
    secondaryCtaPath: '/tms',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  aboutHero: {
    status: 'published',
    public: true,
    eyebrow: "About Magellan International",
    title: "The business behind trucking.",
    body: "Magellan International helps US trucking companies organize the work behind every load: operational documentation, team training and clear responsibility for daily decisions.\n\nOur team’s experience includes work with more than 30 companies before Magellan International was established. Each member brings more than 10 years of experience.",
    imageUrl: '/images/about/truck-road.jpg',
    imageAlt: 'Truck moving through a dark open road landscape',
    primaryCtaLabel: "View Paperwork Packages",
    primaryCtaPath: "/paperwork",
    secondaryCtaLabel: "",
    secondaryCtaPath: "",
    tertiaryCtaLabel: "",
    tertiaryCtaPath: "",
  },
  resourcesHero: {
    status: 'published',
    public: true,
    eyebrow: 'Free resources for carrier paperwork',
    title: 'Start with a checklist. See where the work needs structure.',
    body: 'Use free resources to review your current records and identify the next practical step for your company.',
    imageUrl: '/images/resources/resources-hero-documents.png',
    imageAlt: 'Driver files and trucking operations documents on a dark office desk',
    primaryCtaLabel: 'Browse Free Resources',
    primaryCtaPath: '#resource-library',
    secondaryCtaLabel: 'View Paperwork Packages',
    secondaryCtaPath: '/paperwork',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  paperworkHero: {
    status: 'published',
    public: true,
    eyebrow: 'Paperwork packages for motor carriers',
    title: 'Choose the paperwork system that fits how your carrier operates.',
    body: 'Start with core records, build a management system, or prepare for stricter freight and shipper review.',
    imageUrl: '',
    imageAlt: '',
    primaryCtaLabel: 'Compare Packages',
    primaryCtaPath: '#compare',
    secondaryCtaLabel: 'Request Company Review',
    secondaryCtaPath: '/companies/apply',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  homePathways: { status: 'published', eyebrow: "Services and resources", title: "Find the people and support your office needs.", body: "Arrange Safety or ELD staff through Magellan, recruit someone your carrier hires directly, or set up monthly reviews. Explore the existing service and resource options below to find the support that fits your company." },
  servicesIntroduction: {"status": "published", "eyebrow": "People, oversight and software", "title": "Two hiring routes for your office.", "body": "Choose staff through Magellan International, or pay a one-time recruitment fee to find someone your company hires directly. Candidates we introduce are assessed through our selection process, including those with experience. Full role training for a direct-hire recruit is optional and costs the hiring company extra.\n\nTell us which duties and shifts you need covered, or which areas you want reviewed each month. We agree the service and fees before work starts. For staffing, your company's contract sets the responsibilities, working days and hours."},
  // L6/L7: editable main texts (defaults = the current page texts) and the Partner questionnaires (one question per line).
  aboutFocus: { status: 'published', eyebrow: '', title: "What Magellan focuses on", body: "We focus on the records, operating procedures and department responsibilities US carriers need to manage daily work. These are the areas that shape our documentation, training and process support." },
  careersIntro: { status: 'published', eyebrow: 'Candidate paths', title: 'Start with Safety or ELD.', body: 'Applications are open worldwide for Safety and ELD. Choose the position that interests you.' },
  partnerReferral: { status: 'published', eyebrow: 'Referral or affiliate partner', title: 'Referral questions', body: 'Who do you work with today (carriers, drivers, brokers, others)?\nHow many trucking companies could you introduce to us each month?\nHow do you reach them (website, social media, events, direct)?\nDo you already refer other services? Which ones?' },
  partnerVendor: { status: 'published', eyebrow: 'Vendor partner', title: 'Vendor questions', body: 'What do you offer (insurance, factoring, ELD, other)?\nWhich states and fleet sizes do you serve?\nWhat would you offer our carrier clients?\nWho is the contact for partnerships?' },
  partnerSpeaker: { status: 'published', eyebrow: 'Course speaker', title: 'Speaker questions', body: 'Which topics can you teach?\nWhat is your trucking or compliance experience?\nHave you taught courses or webinars before? Links if any.\nHow many sessions per month could you do?' },
  partnerReseller: { status: 'published', eyebrow: 'Reseller', title: 'Reseller questions', body: 'Which of our services would you resell?\nWho are your clients and where are they?\nHow many clients do you expect in the first 3 months?\nDo you need training or sales material from us?' },
  servicesHero: {
    status: 'published',
    public: true,
    eyebrow: "Services for US carriers",
    title: "Trucking office staffing and operational support.",
    body: "Arrange Safety and ELD staff for your US trucking company, recruit for direct hire or set up monthly reviews. CarrierFlow TMS brings your teams' daily work into one system.",
    imageUrl: '/images/companies/company-hero-desk.png',
    imageAlt: 'Trucking company operations desk with files, laptop, and fleet yard',
    primaryCtaLabel: "Explore services",
    primaryCtaPath: "#core-service-areas",
    secondaryCtaLabel: "View paperwork packages",
    secondaryCtaPath: "/paperwork",
    tertiaryCtaLabel: "",
    tertiaryCtaPath: "",
  },
  webinarsHero: {
    status: 'published',
    public: true,
    eyebrow: "Recorded courses for trucking teams",
    title: "Practical courses for US trucking operations.",
    body: [
      "Learn the office work behind a US trucking operation: driver-document checks, Safety follow-up, recruiting, driver instruction and dispatch handoffs.",
      "A recorded course costs $299 per person; the current ten-course series costs $1,499 per person. Both include videos and accompanying learning material, with three months of access starting when Magellan International receives payment.",
    ].join('\n\n'),
    imageUrl: '/images/webinars/webinars-hero-desk.png',
    imageAlt: 'Trucking operations course desk with laptop, headset, training materials, and fleet yard',
    primaryCtaLabel: "Explore courses",
    primaryCtaPath: '#webinar-series',
    secondaryCtaLabel: "View the full series",
    secondaryCtaPath: "/webinars/complete-webinar-series",
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  careersHero: {
    status: 'published',
    public: true,
    eyebrow: 'Work With Us',
    title: "Work from home in Safety and ELD.",
    body: [
      "Learn how US carriers operate and prepare for Safety and electronic logging device (ELD) support roles, from driver paperwork to daily log support.",
      "We provide learning materials and prepare suitable candidates for referral. If hired, you work directly for a US-based carrier.",
    ].join('\n\n'),
    imageUrl: '/images/careers/work-with-us-hero.jpg',
    imageAlt: 'Organized office desk overlooking a trucking yard at sunset',
    primaryCtaLabel: "Start Your Application",
    primaryCtaPath: '#application-form',
    secondaryCtaLabel: "Explore the Opportunity",
    secondaryCtaPath: '#roles',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  contactHero: {
    status: 'published',
    public: true,
    eyebrow: 'CONTACT',
    title: 'Tell us what your carrier needs to organize next.',
    body: [
      'Use this page for paperwork package questions, course questions, support, scheduling questions, and direct contact with Magellan.',
      'For carrier reviews or work applications, use the dedicated intake path so the request reaches the right workflow faster.',
    ].join('\n\n'),
    imageUrl: '',
    imageAlt: '',
    primaryCtaLabel: 'Company Review',
    primaryCtaPath: '/companies/apply',
    secondaryCtaLabel: 'Contact Us',
    secondaryCtaPath: '/#contact',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
  companiesHero: {
    status: 'published',
    public: true,
    eyebrow: 'Company review for motor carriers',
    title: 'Use this when the package fit is not clear.',
    body: 'Tell us what your carrier needs to control, where the paperwork is breaking down, and whether Basic, Standard, Premium, training, or custom support is the right next step.',
    imageUrl: '/images/companies/company-hero-desk.png',
    imageAlt: 'Organized trucking company operations desk with files, laptop, and fleet yard',
    primaryCtaLabel: 'Start Company Review',
    primaryCtaPath: '#company-form',
    secondaryCtaLabel: 'View Paperwork Packages',
    secondaryCtaPath: '/paperwork',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
  },
};

const LEGACY_SITE_CONTENT_DEFAULTS = {
  homeHero: {
    title: 'Build the operating structure behind your trucking company.',
    body: 'Magellan helps carriers turn paperwork, safety, training, recruiting, TMS workflow, and management visibility into a system the company can actually run.',
  },
  resourcesHero: {
    title: 'Start with a checklist. Build the system when the gaps become clear.',
    body: 'Use these free resources to spot paperwork problems before deciding whether you need Basic, Standard, Premium, targeted training, or company review.',
  },
  webinarsHero: {
    title: 'Courses for carrier teams that need practical systems.',
    body: 'Magellan courses are built for carrier owners, safety managers, dispatch leaders, recruiting teams, compliance staff, and operations managers. Each session focuses on one real operating problem and turns it into a practical company process.',
  },
  companiesHero: {
    title: 'Use this when the package fit is not clear.',
    body: 'Tell us what your carrier needs to control, where the paperwork is breaking down, and whether Basic, Standard, Premium, training, or custom support is the right next step.',
  },
};

function getDbOrThrow() {
  const db = getExternalDb();
  if (!db) {
    throw new Error('Site content CMS is not configured. Check Firebase environment variables.');
  }
  return db;
}

function sanitizeText(value) {
  return String(value || '').trim();
}

function normalizePath(value, fallback = '') {
  const path = sanitizeText(value);
  if (!path) return '';
  if (path.startsWith('#')) return path;
  return path.startsWith('/') && !path.startsWith('//') ? path : fallback;
}

function hasOwn(object, field) {
  return Object.prototype.hasOwnProperty.call(object, field);
}

function normalizeOptionalText(source, field, fallback) {
  return hasOwn(source, field) ? sanitizeText(source[field]) : fallback;
}

function normalizeOptionalPath(source, field, fallback) {
  return hasOwn(source, field) ? normalizePath(source[field], '') : fallback;
}

export function splitSiteContentParagraphs(value) {
  return sanitizeText(value)
    .split(/\n{2,}/)
    .map(sanitizeText)
    .filter(Boolean);
}

export function getDefaultSiteContent(key) {
  return DEFAULT_SITE_CONTENT[key] || DEFAULT_SITE_CONTENT.homeHero;
}

function withContentSource(content, source, fallbackReason = '') {
  return {
    ...content,
    __source: source,
    __fallbackReason: fallbackReason,
  };
}

function normalizeContent(key, data = {}) {
  const fallback = getDefaultSiteContent(key);
  const rawSource = data && typeof data === 'object' ? data : {};
  const legacyDefault = LEGACY_SITE_CONTENT_DEFAULTS[key];
  const source = legacyDefault
    && sanitizeText(rawSource.title) === legacyDefault.title
    && sanitizeText(rawSource.body) === legacyDefault.body
    ? {
      ...rawSource,
      eyebrow: fallback.eyebrow,
      title: fallback.title,
      body: fallback.body,
      imageUrl: sanitizeText(rawSource.imageUrl),
      imageAlt: sanitizeText(rawSource.imageAlt),
      primaryCtaLabel: fallback.primaryCtaLabel,
      primaryCtaPath: fallback.primaryCtaPath,
      secondaryCtaLabel: fallback.secondaryCtaLabel,
      secondaryCtaPath: fallback.secondaryCtaPath,
      tertiaryCtaLabel: fallback.tertiaryCtaLabel,
      tertiaryCtaPath: fallback.tertiaryCtaPath,
    }
    : rawSource;
  const status = sanitizeText(source.status) === 'draft' ? 'draft' : 'published';

  return {
    ...fallback,
    status,
    public: status === 'published',
    eyebrow: normalizeOptionalText(source, 'eyebrow', fallback.eyebrow),
    title: sanitizeText(source.title) || fallback.title,
    body: normalizeOptionalText(source, 'body', fallback.body),
    imageUrl: key.endsWith('Hero') ? sanitizeText(source.imageUrl) : sanitizeText(source.imageUrl) || fallback.imageUrl,
    imageAlt: key.endsWith('Hero') ? sanitizeText(source.imageAlt) : sanitizeText(source.imageAlt) || fallback.imageAlt,
    primaryCtaLabel: normalizeOptionalText(source, 'primaryCtaLabel', fallback.primaryCtaLabel),
    primaryCtaPath: normalizeOptionalPath(source, 'primaryCtaPath', fallback.primaryCtaPath),
    secondaryCtaLabel: normalizeOptionalText(source, 'secondaryCtaLabel', fallback.secondaryCtaLabel),
    secondaryCtaPath: normalizeOptionalPath(source, 'secondaryCtaPath', fallback.secondaryCtaPath),
    tertiaryCtaLabel: normalizeOptionalText(source, 'tertiaryCtaLabel', fallback.tertiaryCtaLabel),
    tertiaryCtaPath: normalizeOptionalPath(source, 'tertiaryCtaPath', fallback.tertiaryCtaPath),
    updatedAt: source.updatedAt || null,
    updatedBy: sanitizeText(source.updatedBy),
  };
}

export function buildEditableSiteContent(key, data) {
  return normalizeContent(key, data || getDefaultSiteContent(key));
}

export async function getPublicSiteContent(key) {
  try {
    if (['homeSpeakers','homeMasterclass','homePathways','servicesIntroduction','servicesHero','aboutFocus','careersIntro','partnerReferral','partnerVendor','partnerSpeaker','partnerReseller'].includes(key)) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`/api/site/v1/content-blocks/${key}`, { credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal });
        const result = await response.json();
        if (!response.ok || !Object.hasOwn(result, 'data')) throw Error('Content unavailable');
        return result.data?.status === 'published' ? withContentSource(normalizeContent(key, result.data), 'cms') : withContentSource(getDefaultSiteContent(key), 'default', 'missing-or-unpublished-document');
      } finally { clearTimeout(timer); }
    }
    if (key === 'webinarsHero') {
      const record = await requestPublicCourse('/hero');
      return record?.status === 'published'
        ? withContentSource(normalizeContent(key, record), 'cms')
        : withContentSource(getDefaultSiteContent(key), 'default', 'missing-or-unpublished-document');
    }
    const db = getDbOrThrow();
    const snapshot = await getDoc(doc(db, SITE_CONTENT_COLLECTION, key));
    if (!snapshot.exists()) {
      return withContentSource(getDefaultSiteContent(key), 'default', 'missing-document');
    }

    const content = normalizeContent(key, snapshot.data());
    return content.status === 'published'
      ? withContentSource(content, 'cms')
      : withContentSource(getDefaultSiteContent(key), 'default', 'draft-document');
  } catch (error) {
    if (String(error?.message || '').includes('Site content CMS is not configured')) {
      return withContentSource(getDefaultSiteContent(key), 'emergency-default', 'cms-not-configured');
    }
    console.error(`Failed to load site content [${key}]:`, error);
    return withContentSource(getDefaultSiteContent(key), 'emergency-default', 'load-error');
  }
}

export async function getAdminSiteContent(key) {
  if (!DEFAULT_SITE_CONTENT[key]) throw new Error('Unknown site content block.');
  const db = getDbOrThrow();
  const snapshot = await getDoc(doc(db, SITE_CONTENT_COLLECTION, key));
  return buildEditableSiteContent(key, snapshot.exists() ? snapshot.data() : getDefaultSiteContent(key));
}

export async function saveSiteContent({ key, content, adminEmail = '' }) {
  if (!DEFAULT_SITE_CONTENT[key]) {
    throw new Error('Unknown site content block.');
  }

  const db = getDbOrThrow();
  const saved = await getDoc(doc(db, SITE_CONTENT_COLLECTION, key));
  if (key.endsWith('Hero') && ['imageUrl', 'imageAlt'].some(field => (content[field] || '') !== (saved.data()?.[field] || '')))
    throw new Error('Edit this image in Site content’s page media controls.');
  const title = sanitizeText(content.title);

  if (!title) {
    throw new Error('Title is required.');
  }

  const normalized = normalizeContent(key, content);
  const payload = {
    ...normalized,
    public: normalized.status === 'published',
    updatedAt: serverTimestamp(),
    updatedBy: sanitizeText(adminEmail),
  };

  await setDoc(doc(db, SITE_CONTENT_COLLECTION, key), payload, { merge: true });
  await writeAdminAuditEvent({
    action: payload.public ? 'published' : 'saved-draft',
    targetCollection: SITE_CONTENT_COLLECTION,
    targetId: key,
    summary: 'Site content block saved',
    adminEmail,
    metadata: {
      route: SITE_CONTENT_KEYS.find((item) => item.key === key)?.route || '/',
      status: payload.status,
      title: payload.title,
    },
  });
  if (payload.public) {
    const route = SITE_CONTENT_KEYS.find((item) => item.key === key)?.route || '/';
    await markStaticSeoRefreshRequired({
      action: 'updated',
      source: 'site-content',
      path: route,
      title: payload.title,
      adminEmail,
    }).catch((error) => {
      console.error('Failed to mark static SEO refresh required after site content update:', error);
    });
  }
  return payload;
}
